import { Alert, Box, Tooltip } from "@mui/material";
import { CopyableText } from "components/CopyableText";
import { CredentialPortalPicker } from "components/Credentials/CredentialPortalPicker";
import { EditablePasswordField } from "components/Credentials/EditablePasswordField";
import { SecurityQuestionsField } from "components/Credentials/SecurityQuestionsField";
import { CheckboxField } from "components/Forms/CheckboxField";
import { FormAutocomplete } from "components/Forms/FormAutocomplete";
import { FormTextField } from "components/Forms/FormTextField";
import { CredentialInputForValidation, CredentialKind } from "generated-graphql/graphql";
import { Control, useWatch } from "react-hook-form";
import { credentialKindToLabel } from "util/constants";

import { CredentialTenancy } from "./types";

const KIND_ITEMS = Object.values(CredentialKind).map((kind) => ({
  value: kind,
  label: credentialKindToLabel(kind),
}));

/**
 * The box is the control for tenancy: ticked means shared, unticked hands the credential to the
 * single tenant behind its facilities. Naming that tenant is the only case worth helper text —
 * none and several are both blocked, and the rule's own message says why.
 */
function EncampOwnedField({
  control,
  disabled,
  tenancy,
}: {
  control: Control<CredentialInputForValidation>;
  disabled: boolean;
  tenancy: CredentialTenancy;
}) {
  const isEncampOwned = useWatch({ control, name: "isEncampOwned" });
  const namesTenant = !isEncampOwned && tenancy.count === 1 && !!tenancy.name;

  return (
    <Box sx={{ mt: 2 }}>
      <CheckboxField
        control={control}
        name="isEncampOwned"
        label="Encamp Owned"
        disabled={disabled}
        staticErrorSpacing={false}
      />
      {isEncampOwned && (
        <Alert severity="info" sx={{ mt: 0.5 }}>
          Non-Encamp users will not be able to see this credential.
        </Alert>
      )}
      {namesTenant && (
        <Alert severity="info" sx={{ mt: 0.5 }}>
          Tenant-owned — {tenancy.name} will be able to see this credential.
        </Alert>
      )}
    </Box>
  );
}

/** The copy icon sits in the label rather than the input, which holds a masked value. */
function CopyableLabel({
  text,
  value,
  show,
}: {
  text: string;
  value?: string | null;
  show: boolean;
}) {
  return (
    <>
      {text}
      {show && <CopyableText hideText>{value}</CopyableText>}
    </>
  );
}

/** Two are jurisdiction-gated and one is kind-gated, so each stands on its own. */
function PortalFlagFields({
  control,
  kind,
  jurisdiction,
}: {
  control: Control<CredentialInputForValidation>;
  kind?: CredentialKind | null;
  jurisdiction?: string | null;
}) {
  return (
    <>
      {jurisdiction === "WV" && (
        <CheckboxField
          control={control}
          name="usesCombinedInvoicing"
          label="Uses Combined Invoicing (WV only)"
        />
      )}
      {jurisdiction === "AZ" && (
        <CheckboxField
          control={control}
          name="usesRcoCredential"
          label="RCO Credential (AZ only)"
        />
      )}
      {kind === CredentialKind.RcraFederal && (
        <Tooltip title="Encamp will use this credential to sync manifests and site access from RCRAInfo. Only enable if this account has e-Manifest API keys.">
          <Box>
            <CheckboxField control={control} name="usesEManifestApi" label="Uses e-Manifest API" />
          </Box>
        </Tooltip>
      )}
    </>
  );
}

type CredentialFieldsProps = {
  control: Control<CredentialInputForValidation>;
  kind?: CredentialKind | null;
  disabled: boolean;
  /** Saving or loading, without the read-only component. */
  busy: boolean;
  mode?: "add" | "edit";
  passwordHelperText?: string;
  canViewCredentials: boolean;
  showPasswordCopyIcon: boolean;
  passwordCopyValue: string | null;
  onPasswordEdit: () => void;
  onPasswordCancelEdit: () => void;
  storedPortalId?: string | null;
  usernameCopyValue?: string | null;
  emailCopyValue?: string | null;
  /** Only read when `showPortalFlags` is set — two of the flags are jurisdiction-gated. */
  selectedPortalJurisdiction?: string | null;
  onJurisdictionChange?: (jurisdiction: string | null) => void;
  showOwnerControl: boolean;
  tenancy: CredentialTenancy;
  showSecurityQuestions: boolean;
  /** `usesCombinedInvoicing` / `usesRcoCredential` / `usesEManifestApi`. */
  showPortalFlags: boolean;
  /** A slot, not a flag: `CredentialVerification` is bound to the customer document. */
  verification?: React.ReactNode;
};

/**
 * The credential field column, shared by the customer dialog (which the staff EPCRA
 * drawer also renders) and the staff Credential Management modal. Dialog shells and
 * facility panels stay with each surface.
 */
export function CredentialFields({
  control,
  kind,
  disabled,
  busy,
  mode,
  passwordHelperText,
  canViewCredentials,
  showPasswordCopyIcon,
  passwordCopyValue,
  onPasswordEdit,
  onPasswordCancelEdit,
  storedPortalId,
  usernameCopyValue,
  emailCopyValue,
  selectedPortalJurisdiction,
  onJurisdictionChange,
  showOwnerControl,
  tenancy,
  showSecurityQuestions,
  showPortalFlags,
  verification,
}: CredentialFieldsProps) {
  return (
    <>
      <FormTextField
        name="name"
        label="Name"
        disabled={disabled}
        control={control}
        textFieldProps={{ required: true }}
      />
      <FormTextField
        sx={{ mt: 0.5 }}
        disabled={disabled}
        name="username"
        label={
          <CopyableLabel text="Username" value={usernameCopyValue} show={canViewCredentials} />
        }
        control={control}
        textFieldProps={{ required: true }}
      />
      <EditablePasswordField
        sx={{ mt: 0.5 }}
        mode={mode}
        name="password"
        label={
          <CopyableLabel text="Password" value={passwordCopyValue} show={showPasswordCopyIcon} />
        }
        helperText={passwordHelperText}
        disabled={disabled}
        canViewCredentials={canViewCredentials}
        onEditClick={onPasswordEdit}
        onCancelEdit={onPasswordCancelEdit}
        required
      />
      <FormAutocomplete
        sx={{ mt: 0.5 }}
        disabled={disabled}
        name="kind"
        label="Type"
        required
        autocompleteItems={KIND_ITEMS}
        control={control}
        rules={{ required: true }}
      />
      <FormTextField
        sx={{ mt: 0.5 }}
        disabled={disabled}
        control={control}
        name="email"
        label={<CopyableLabel text="Email" value={emailCopyValue} show={canViewCredentials} />}
      />

      <CredentialPortalPicker
        kind={kind}
        control={control}
        disabled={disabled}
        storedPortalId={storedPortalId}
        onJurisdictionChange={onJurisdictionChange}
      />
      {showPortalFlags && (
        <PortalFlagFields control={control} kind={kind} jurisdiction={selectedPortalJurisdiction} />
      )}

      {verification}
      {showOwnerControl && <EncampOwnedField control={control} disabled={busy} tenancy={tenancy} />}
      {showSecurityQuestions && (
        <SecurityQuestionsField
          name="securityQuestions"
          control={control}
          disabled={busy}
          canViewCredentials={canViewCredentials}
        />
      )}
    </>
  );
}
